import React, { useState, useEffect, useContext } from "react";
import { makeStyles } from "@material-ui/styles";
import { Grid, Box, FormControl, FormLabel, FormGroup, FilledInput, Tooltip, IconButton, Icon } from "@material-ui/core";
import { DataGrid } from "@material-ui/data-grid";
import { useLocation, Link } from "react-router-dom";

import Loader from "../../common/loader";
import Crud from "../../common/crud";
import NotFound from "../../common/not-found";
import ViewTitle from "../../common/view-title";
import Trash from "../../common/trash";

import AuthContext from "../../context";
import { del, get, post, put } from "../../utils/api-services";
import helpers from "../../utils/helpers";

const useStyles = makeStyles(() => ({
	root: {
		display: "flex",
		flexDirection: "column",
		flex: 1,
		paddingBottom: 20,
	},
	tableContainer: {
		width: "100%",
	},
}));

const Items = () => {
	const { showToast, signOut, showLoader } = useContext(AuthContext);
	const classes = useStyles();
	const { pathname } = useLocation();
	const [state, setState] = useState({
		loading: true,
		items: [],
		item: {
			idff_item: 0,
			name: "",
		},
		permissions: {},
	});

	useEffect(() => {
		window.document.title = "Items";

		validatePermissions();
	}, []);

	const validatePermissions = () => {
		const permissions = helpers.getPermissions(pathname);
		setState((prev) => ({ ...prev, permissions }));

		permissions.read_attr ? makeRequest() : setState((prev) => ({ ...prev, loading: false }));
	};

	const makeRequest = async () => {
		showLoader(true);
		const response = await get("/farmForest/item");
		if (response.status === 200) {
			setState((prev) => ({ ...prev, loading: false, items: response?.data ?? [] }));
		} else {
			console.log("[Items] makeRequest error", response);
			helpers.failRequest(response, showToast, signOut);
		}
		showLoader(false);
	};

	const handleChange = ({ target }) => setState((prev) => ({ ...prev, item: { ...prev.item, [target.name]: target.value } }));

	const validateForm = (type) => {
		const { idff_item, name } = state.item;

		switch (type) {
			case "update":
				const data = state.items.filter((value) => value.idff_item === idff_item);

				if (data[0]) {
					const before = JSON.stringify(data[0]);
					const after = JSON.stringify(state.item);
					if (before !== after) {
						if (name.trim()) {
							return true;
						} else {
							showToast("All fields are required", "error");
							return false;
						}
					} else {
						showToast("Update some field", "error");
						return false;
					}
				} else {
					showToast("Select some item", "error");
					return false;
				}

			case "store":
				if (name.trim()) {
					return true;
				} else {
					showToast("All fields are required", "error");
					return false;
				}

			case "delete":
				if (idff_item === 0) {
					showToast("Select some item", "error");
					return false;
				}
				const cond = window.confirm("Are you sure you want to delete this item?");

				return Boolean(idff_item && cond);
			default:
				break;
		}
	};

	const clear = () => {
		setState((prev) => ({ ...prev, item: { ...prev.item, idff_item: 0, name: "" } }));
	};

	const store = async () => {
		const cond = validateForm("store");

		if (cond) {
			showLoader(true);
			const response = await post("/farmForest/item/store", state.item);
			if (response.status === 200) {
				setState((prev) => ({ ...prev, items: response?.data ?? prev.items, item: { idff_item: 0, name: "" } }));
				showToast(response.message);
			} else {
				console.log("[Item] store error", response);
				helpers.failRequest(response, showToast, signOut);
				showLoader(false);

				return false;
			}
			showLoader(false);
		}

		return cond;
	};

	const update = async () => {
		const { idff_item } = state.item;
		const cond = validateForm("update");

		if (cond) {
			showLoader(true);
			const response = await put(`/farmForest/item/${idff_item}`, state.item);
			if (response.status === 200) {
				setState((prev) => ({ ...prev, idems: response?.data ?? prev.items, item: { idff_item: 0, name: "" } }));
				showToast(response.message);
			} else {
				console.log("[Item] update error", response);
				helpers.failRequest(response, showToast, signOut);
			}
			showLoader(false);
		}
	};

	const destroy = async () => {
		const { idff_item } = state.item;
		const cond = validateForm("delete");

		if (cond) {
			showLoader(true);
			const response = await del(`/farmForest/item/${idff_item}`);
			if (response.status === 200) {
				setState((prev) => ({ ...prev, items: response?.data ?? prev.items, item: { idff_item: 0, name: "" } }));
				showToast(response.message);
			} else {
				console.log("[Item] destroy error", response);
				helpers.failRequest(response, showToast, signOut);
			}
			showLoader(false);
		}
	};

	const renderCell = ({ row }) => {
		return (
			<Link to={`/history/ff_item/${row.idff_item}`} target="_blank">
				<Tooltip title="Show history" placement="top">
					<IconButton>
						<Icon>{"history"}</Icon>
					</IconButton>
				</Tooltip>
			</Link>
		);
	};

	const columns = [
		{ field: "name", headerName: "Name", flex: 1 },
		{ field: "history", headerName: "Actions", flex: 0.3, renderCell },
	];

	if (state.loading) return <Loader />;

	if (!state.permissions.read_attr) return <NotFound />;

	return (
		<div className={classes.root}>
			<ViewTitle sedeFlag title="Items" />
			<div>
				<Grid container>
					<Grid item xs={12} md={6}>
						<FormGroup>
							<FormLabel>{"Name"}</FormLabel>
							<FormControl variant="filled" component={Box} width="100%">
								<FilledInput autoComplete="off" type="text" placeholder="Name" name="name" value={state.item.name} onChange={handleChange} />
							</FormControl>
						</FormGroup>
					</Grid>
				</Grid>
			</div>

			<Crud permissions={state.permissions} create={{ create: clear, store }} update={update} destroy={destroy} cancel={clear} />

			{state.permissions.delete_attr && <Trash table="ff_item" id="idff_item" onRefresh={makeRequest} />}

			<div className={classes.tableContainer}>
				<DataGrid
					rows={state.items}
					columns={columns}
					pageSize={45}
					density="compact"
					autoHeight
					getRowId={(row) => row.idff_item}
					loading={state.loading}
					onRowClick={({ row }) => setState((prev) => ({ ...prev, item: row }))}
				/>
			</div>
		</div>
	);
};

export default Items;
